import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type RoleFormSchema, roleSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { RoleForm } from "@/forms/role-form";
import { client } from "@/hono";

/** Props Interface */
interface AddRoleModalProps extends ModalProps {
	/** Receives the created role, so the surface that opened the modal can select it. */
	onComplete?: (role: { id: string }) => void | Promise<void>;
}

export function AddRoleModal({ isOpen, onClose, onComplete }: AddRoleModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm({
		resolver: resolver(roleSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: RoleFormSchema) {
		const response = await client.roles.index.$post({
			json,
		});
		const payload = await responseHandler(response);
		// The table's own refetch is not enough: the new role has to appear in every list that
		// names roles — the individual permission form and the caller's resolved abilities —
		// and those hang off other keys. Awaited before `onComplete`, so a caller selecting the
		// created role finds it already in the refetched list.
		await qc.invalidateQueries({
			predicate: invalidate(["roles", "citadel", "user-permissions-form"]),
		});
		await onComplete?.(payload.role);
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("roles.create_role")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit, console.error)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<RoleForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
